/**
 * Free Tier Dashboard Types
 * TypeScript interfaces for free tier user monitoring
 */

export interface FeatureRun {
  period: string;
  request_type: string;
  runs: number;
}

export interface UseCaseData {
  usecase: string;
  total_runs: number;
  unique_users: number;
  active_days: number;
}

export interface FreeTierUser {
  id: number;
  email: string;
  name: string | null;
  signup_date: string;
  simulation_count: number;
  chat_session_count: number;
  last_activity: string | null;
  primary_usecase: string | null;
}

export interface UserDetail {
  id: number;
  email: string;
  name: string | null;
  subscription_plan: string;
  subscription_status: string;
  created_at: string;
  monthly_simulation_count: number;
  total_audience_requests: number;
}

export interface ActivityLogEntry {
  id: number;
  created_at: string;
  request_type: string;
  usecase: string | null;
  status: string;
  model: string;
  time_taken_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost: number | null;
}

export interface SimulationPreview {
  id: number;
  name: string;
  use_case: string | null;
  created_at: string;
  goal_of_simulation: string | null;
  message_to_test: string | null;
  response_preview: string | null;
}

export interface ChatHistoryEntry {
  id: number;
  chat_data: ChatMessage[];
  created_at: string;
}

export interface ChatMessage {
  role: string;
  content: string;
  timestamp?: string;
}

export interface PersonaData {
  id: number;
  name: string;
  data: string | Record<string, any>;
}

export interface SimulationFullContent {
  id: number;
  name: string;
  description: string | null;
  use_case: string | null;
  goal_of_simulation: string | null;
  message_to_test: string | null;
  content: Record<string, any> | null;
  simulation_response: string | null;
  expert_reflections: string | null;
  source_model: string | null;
  created_at: string;
  chat_histories: ChatHistoryEntry[];
  personas: PersonaData[];
}

export interface ChatSession {
  id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatSessionFull {
  id: number;
  user_id: number;
  title: string | null;
  messages: ChatMessage[] | string | null; // Can be array, JSON string, or null from backend
  created_at: string;
  updated_at: string;
}

export interface SummaryStats {
  new_users: number;
  active_users: number;
  total_free_users: number;
  total_runs: number;
  total_simulations: number;
  total_chats: number;
  most_used_feature: string | null;
  least_used_feature: string | null;
  feature_breakdown: Record<string, number>;
}

export interface CostByModel {
  model: string;
  cost: number;
  requests: number;
  input_tokens: number;
  output_tokens: number;
}

export interface CostOverTime {
  period: string;
  cost: number;
  requests: number;
}

export interface CostData {
  total_cost: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_requests: number;
  cost_by_model: CostByModel[];
  cost_over_time: CostOverTime[];
}

export interface UseCaseCostItem {
  parent_usecase: string;
  sub_step: string | null;
  query_count: number;
  total_cost: number;
  avg_cost: number;
}

export interface UseCaseRunCount {
  name: string;
  count: number;
}

export interface UserCostData {
  id: number;
  email: string;
  name: string | null;
  total_queries: number;
  total_cost: number;
  simulation_count: number;
  chat_count: number;
}

export interface FreeTierDashboardData {
  time_range: string;
  granularity: string;
  start_date: string;
  summary: SummaryStats;
  feature_runs: FeatureRun[];
  usecase_breakdown: UseCaseData[];
  users: FreeTierUser[];
  cost_data: CostData;
  usecase_cost_breakdown: UseCaseCostItem[];
  cost_by_user: UserCostData[];
  usecase_run_counts: UseCaseRunCount[];
}

export interface UserDetailResponse {
  time_range: string;
  granularity: string;
  start_date: string;
  user: UserDetail;
  feature_runs: FeatureRun[];
  usecase_breakdown: UseCaseData[];
  activity_log: ActivityLogEntry[];
  simulations: SimulationPreview[];
  chat_sessions: ChatSession[];
  cost_data: CostData;
  usecase_run_counts: UseCaseRunCount[];
  usecase_cost_breakdown: UseCaseCostItem[];
}

export type TimeRange = '24h' | '7d' | '30d' | '90d' | '1y';

export interface TimeRangeOption {
  value: TimeRange;
  label: string;
  granularity: string;
}

export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { value: '24h', label: '24 Hours', granularity: 'hour' },
  { value: '7d', label: '7 Days', granularity: 'day' },
  { value: '30d', label: '30 Days', granularity: 'day' },
  { value: '90d', label: '90 Days', granularity: 'week' },
  { value: '1y', label: '1 Year', granularity: 'month' },
];

export const FEATURE_LABELS: Record<string, string> = {
  simulation_analysis: 'Simulations',
  group_chat: 'Group Chats',
  healthcare_chat: 'Healthcare Chat',
  healthcare_v2: 'Healthcare v2',
  persona_chat: 'Persona Chats',
  survey_response: 'Survey Responses',
  persona_generation: 'Persona Generation',
  segment_generation: 'Segment Generation',
  summary_generation: 'Summaries',
};

export const USECASE_LABELS: Record<string, string> = {
  Survey: 'Survey & Focus Groups',
  'Packaging Test': 'Packaging Test',
  'AB Test': 'AB Test / Multivariate',
  'Creative Test': 'Creative Test',
  'Pricing Test': 'Pricing Test',
  'Concept Test': 'Concept Test',
  'Claims Test': 'Claims Test',
  'Branding Test': 'Branding Test',
  'Messaging Test': 'Messaging Test',
  'Healthcare v2': 'Healthcare v2',
  audience: 'Audience',
  simulation: 'Simulation',
  chat: 'Chat',
  group_chat: 'Group Chat',
};
